import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "react-query";
import { DragDropContext } from "react-beautiful-dnd";
import agenda from "./Agenda_Icon.svg";
import { Header } from "../Shared/Header/Header";

//mock json data
import { tempArchivedAgendas } from "./mockData";
import { useDashboard } from "ManagementTeam/DashboardPage/useDashboard";
import { useAgenda } from "./useAgenda";
import { useSubmittedAgenda } from "../AgendaPage/SubmittedAgendaItemsPage/useSubmittedAgenda";

import TabBar from "ManagementTeam/Shared/TabBar";
import Agenda from "./Agenda";
import ArchivedAgendaPage from "./ArchivedAgendaPage";
import SubmittedAgendaItemsPage from "./SubmittedAgendaItemsPage";
import styles from "./AgendaPage.module.css";
import Auth from "shared/@auth";
import { toTitleCase } from "shared/@utils/formatString";
import { getPdfFutureMeeting } from "../utils/getPdfFutureMeeting";
import { useDocuments } from "ManagementTeam/DocumentsPage/useDocuments";

export function AgendaPage({
  meetingType,
  isNewSubmitted,
  isHideInfo,
  setToggleInfo,
}) {
  const { hasPermission } = Auth.useContainer();
  const readOnly = !hasPermission("management-team.admins");
  const [refetchInterval, setRefetchInterval] = useState(1000 * 10);
  const [refetchIntervalSubmitted, setRefetchIntervalSubmitted] = useState(
    1000 * 60
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // refetch interval can be set to ms or 0/false to disable
  const {
    data: dashboardData,
    isFetching: isDashboardFetching,
    status,
    refetchDashboard,
  } = useDashboard(refetchInterval, true, true); // explicitly enable on Agenda page
  const {
    submittedAgendaData,
    submittedAgendaEditPut,
    submittedAgendaDelete,
    submittedAgendaMove,
    submittedAgendaOrder,
    submittedAgendaViewPut,
    refetchAgenda,
  } = useSubmittedAgenda(refetchIntervalSubmitted);
  const { archiveAgendaData, refetchArchive } = useAgenda({
    enableArchiveAgenda: true,
  });
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);

  const [futureMeetingsForEC, setFutureMeetingsForEC] = useState([]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Ensure no overlapping submittedAgenda requests; then refetch all once
      await queryClient.cancelQueries(["submittedAgenda"]);
      await Promise.all([
        refetchDashboard(),
        refetchAgenda(),
        refetchArchive(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    if (meetingType !== "EC") return;

    const meetings = (
      (dashboardData && dashboardData.futureMeetings) ||
      []
    ).map((a) => getPdfFutureMeeting(a));

    setFutureMeetingsForEC(meetings);
  }, [dashboardData, dashboardData.futureMeetings]);

  useEffect(() => {
    setData(
      dashboardData && dashboardData.meetings ? dashboardData.meetings : []
    );
  }, [dashboardData]);

  useEffect(() => {
    setUsers(
      dashboardData && dashboardData.users ? [...dashboardData.users] : []
    );
  }, [dashboardData, dashboardData.users]);

  useEffect(() => {
    refetchDashboard();
  }, []);

  //States for the primary agenda page
  const [dropdownData, setDropdownData] = useState(
    data && data.length
      ? data.filter((agenda) => agenda.meetingType === meetingType)
      : []
  );
  const [archiveData, setArchiveData] = useState(
    archiveAgendaData && archiveAgendaData.length
      ? archiveAgendaData.filter((agenda) => agenda.meetingType === meetingType)
      : []
  );

  const [currentPage, setCurrentPage] = useState("Agenda");
  const [itemOwners, setItemOwners] = useState([]);

  useMemo(() => {
    data &&
      setDropdownData(
        data.filter((agenda) => agenda.meetingType === meetingType)
      );
  }, [meetingType, data]);

  useMemo(() => {
    archiveAgendaData &&
      setArchiveData(
        archiveAgendaData.filter((agenda) => agenda.meetingType === meetingType)
      );
  }, [meetingType, archiveAgendaData]);

  const [newSubmittedAgendas, setNewSubmittedAgendas] = useState();

  const handlePageChange = (page) => {
    // eslint-disable-next-line default-case
    switch (page) {
      case "Archived Agendas":
        setDropdownData(
          tempArchivedAgendas.filter(
            (agenda) => agenda.meetingType === meetingType
          )
        );
        setCurrentPage(page);
        break;
      case "Agenda":
        setDropdownData(
          data
            ? data.filter((agenda) => agenda.meetingType === meetingType)
            : []
        );
        setCurrentPage(page);
        break;
      case "Submitted Agenda Items":
        setCurrentPage(page);
        break;
    }
  };

  useMemo(() => {
    if (!itemOwners.length) {
      if (dropdownData.length) {
        const memberNames = dropdownData[0].users
          .filter((member) => member.accessLevel.includes("MT1"))
          .map((member) => {
            const lastName =
              member.profile.lastName.toLowerCase().charAt(0).toUpperCase() +
              member.profile.lastName.toLowerCase().slice(1);
            const firstName =
              member.profile.firstName.toLowerCase().charAt(0).toUpperCase() +
              member.profile.firstName.toLowerCase().slice(1);
            return {
              id: member._id,
              option: `${firstName} ${lastName}`,
              photoUrl: member.profile.photoUrl,
              accessLevel: member.accessLevel,
              firstName: firstName,
              lastName: lastName,
            };
          });

        const attorneysIBO = dropdownData[0].users
          .filter((member) => member.profile.firstName === "Attorneys")
          .map((member) => ({
            id: member._id,
            option: `${member.profile.firstName} ${member.profile.lastName}`,
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName: member.profile.lastName,
            firstName: member.profile.firstName,
          }));

        const wwgIBO = dropdownData[0].users
          .filter(
            (member) =>
              member.profile.firstName === "WWG" ||
              member.profile.firstName === "WWG Guest"
          )
          .map((member) => ({
            id: member._id,
            option: `${member.profile.firstName} ${member.profile.lastName}`,
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName: member.profile.lastName,
            firstName: member.profile.firstName,
          }));

        const tsika = dropdownData[0].users
          .filter(
            (member) =>
              member.profile.firstName.toUpperCase() === "PAUL" &&
              member.profile.lastName.toUpperCase() === "TSIKA"
          )
          .map((member) => ({
            id: member._id,
            option: toTitleCase(
              `${member.profile.firstName} ${member.profile.lastName}`
            ),
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName: member.profile.lastName,
            firstName: member.profile.firstName,
          }));

        setItemOwners(
          [attorneysIBO[0], ...wwgIBO, tsika[0], ...memberNames].sort(
            (a, b) => {
              const result = a.lastName.localeCompare(b.lastName);

              return result !== 0
                ? result
                : a.firstName.localeCompare(b.firstName);
            }
          )
        );
      }
    }
  }, [dropdownData, itemOwners]);

  return (
    <div className={styles.agendaPage}>
      <div className={styles.header}>
        <Header
          title={"Agendas"}
          icon={agenda}
          children={
            status === "success" && (
              <div className={styles.tabBar}>
                <TabBar
                  activeTitle={currentPage}
                  titleList={[
                    { title: "Agenda", notification: 0 },
                    {
                      title: "Submitted Agenda Items",
                      notification: newSubmittedAgendas,
                    },
                    { title: "Archived Agendas", notification: 0 },
                  ]}
                  onChange={handlePageChange}
                  isNewSubmitted={isNewSubmitted}
                />
              </div>
            )
          }
          isLock={true}
          isHide={isHideInfo}
          setToggleInfo={setToggleInfo}
          onRefresh={handleRefresh}
          isLoading={isRefreshing}
        />
      </div>

      {currentPage === "Agenda" && dropdownData && (
        <Agenda
          readOnly={!hasPermission("management-team.admins")}
          agendas={dropdownData}
          status={status}
          itemOwners={itemOwners}
          futureMeetingsForEC={futureMeetingsForEC}
          setIsModalOpen={(e) => {
            setRefetchInterval(e ? false : 1000 * 10);
            setRefetchIntervalSubmitted(e ? false : 1000 * 60);
          }}
          meetingType={meetingType}
          isHideInfo={isHideInfo}
        />
      )}
      {/**Archived agenda page*/}
      {currentPage === "Archived Agendas" && archiveData && (
        <ArchivedAgendaPage
          readOnly={!hasPermission("management-team.admins")}
          selectedArchive={archiveData}
          itemOwners={itemOwners}
          meetingType={meetingType}
          refetchArchive={refetchArchive}
          isHideInfo={isHideInfo}
        />
      )}
      {/**Submitted Agenda Page */}
      {currentPage === "Submitted Agenda Items" && (
        <SubmittedAgendaItemsPage
          readOnly={!hasPermission("management-team.admins")}
          meetings={dropdownData}
          submittedAgendaItems={submittedAgendaData}
          itemOwners={itemOwners}
          meetingType={meetingType}
          users={users}
          submittedAgendaEditPut={submittedAgendaEditPut}
          submittedAgendaDelete={submittedAgendaDelete}
          submittedAgendaMove={submittedAgendaMove}
          submittedAgendaOrder={submittedAgendaOrder}
          submittedAgendaViewPut={submittedAgendaViewPut}
          refetchAgenda={refetchAgenda}
        />
      )}
    </div>
    // </DragDropContext>
  );
}
